import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

/**
 * Generic split-pane viewer shell extracted from
 * `RefactorFileViewerSlide.tsx`. Provides the framed card with a resizable
 * left rail, a right-hand content area, and a centered footer for prev/next
 * navigation — so any "list on the left, detail on the right, walk through
 * items in order" surface (refactor file viewer, migration risk review,
 * future endpoint detail viewers, …) can share the exact same chrome and
 * pixel layout.
 *
 * Slots intentionally accept `ReactNode` so callers can drop in whatever
 * left rail / header / content makes sense; navigation is centralized via
 * `currentIndex` + `totalCount` + `onPrev` / `onNext` so we have a single
 * source of truth for the "left button doesn't move" bug class.
 */
export interface SplitPaneViewerProps {
  /** Outer card width. Defaults to 1400 (matches RefactorFileViewerSlide). */
  width?: number
  /** Outer card height. Defaults to 900. */
  height?: number
  /** Initial sidebar width (px). Defaults to 300. */
  initialSidebarWidth?: number
  minSidebarWidth?: number
  maxSidebarWidth?: number
  /** Whether the sidebar can be resized. Defaults to true. */
  isResizable?: boolean
  sidebarClassName?: string
  contentClassName?: string

  /** Optional fixed-height (80px) sidebar header. */
  sidebarHeader?: ReactNode
  /** The list / tree rendered in the left rail (vertically scrollable). */
  sidebar: ReactNode

  /**
   * Left side of the right-pane header (typically the active item's name).
   * Lives inside an h-[80px] strip with `border-b`.
   */
  headerLeft: ReactNode
  /** Right side of the right-pane header — actions, close button, etc. */
  headerRight?: ReactNode

  /** Main content area (right pane body). Caller controls overflow. */
  content: ReactNode

  /**
   * Footer slot. If omitted, a centered prev/next chevron pair is rendered
   * driven by `currentIndex` + `totalCount` + `onPrev` / `onNext`. Pass
   * `null` to suppress the footer entirely.
   */
  footer?: ReactNode | null
  currentIndex?: number
  totalCount?: number
  onPrev?: () => void
  onNext?: () => void
  /** ARIA label for the prev chevron in the default footer. */
  prevAriaLabel?: string
  /** ARIA label for the next chevron in the default footer. */
  nextAriaLabel?: string
}

const ChevronIcon = ({ rotate }: { rotate?: boolean }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={rotate ? 'rotate-180' : undefined}
  >
    <path
      d='M6 12L10 8L6 4'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

function DefaultFooter({
  currentIndex,
  totalCount,
  onPrev,
  onNext,
  prevAriaLabel = 'Previous item',
  nextAriaLabel = 'Next item'
}: Pick<
  SplitPaneViewerProps,
  'currentIndex' | 'totalCount' | 'onPrev' | 'onNext' | 'prevAriaLabel' | 'nextAriaLabel'
>) {
  const hasIndex = typeof currentIndex === 'number' && typeof totalCount === 'number'
  const prevDisabled = !hasIndex || currentIndex! <= 0 || !onPrev
  const nextDisabled = !hasIndex || currentIndex! >= totalCount! - 1 || !onNext
  return (
    <div className='h-[72px] bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 flex items-center justify-center px-8 py-4 flex-shrink-0'>
      <div className='flex items-center'>
        <button
          onClick={onPrev}
          disabled={prevDisabled}
          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
            prevDisabled
              ? 'cursor-not-allowed opacity-40'
              : 'hover:bg-tokens-neutral-fill-fill-6 cursor-pointer'
          }`}
          aria-label={prevAriaLabel}
        >
          <ChevronIcon rotate />
        </button>
        <button
          onClick={onNext}
          disabled={nextDisabled}
          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
            nextDisabled
              ? 'cursor-not-allowed opacity-40'
              : 'hover:bg-tokens-neutral-fill-fill-6 cursor-pointer'
          }`}
          aria-label={nextAriaLabel}
        >
          <ChevronIcon />
        </button>
      </div>
    </div>
  )
}

export function SplitPaneViewer({
  width = 1400,
  height = 900,
  initialSidebarWidth = 300,
  minSidebarWidth = 200,
  maxSidebarWidth = 600,
  isResizable = true,
  sidebarClassName,
  contentClassName,
  sidebarHeader,
  sidebar,
  headerLeft,
  headerRight,
  content,
  footer,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
  prevAriaLabel,
  nextAriaLabel
}: SplitPaneViewerProps) {
  const [sidebarWidth, setSidebarWidth] = useState(initialSidebarWidth)
  const [isResizing, setIsResizing] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isResizable) return
      e.preventDefault()
      setIsResizing(true)
    },
    [isResizable]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizable || !isResizing || !sidebarRef.current) return
      const containerRect = sidebarRef.current.parentElement?.getBoundingClientRect()
      if (!containerRect) return
      const newWidth = e.clientX - containerRect.left
      const clamped = Math.max(minSidebarWidth, Math.min(maxSidebarWidth, newWidth))
      setSidebarWidth(clamped)
    },
    [isResizable, isResizing, minSidebarWidth, maxSidebarWidth]
  )

  const handleMouseUp = useCallback(() => {
    setIsResizing(false)
  }, [])

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    } else {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  return (
    <div
      className='flex flex-col bg-white rounded-[28px] shadow-[0px_6px_16px_0px_rgba(0,0,0,0.08),0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)] overflow-hidden'
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      <div className='flex flex-1 overflow-hidden relative'>
        {/* Sidebar */}
        <div
          ref={sidebarRef}
          className={`flex-shrink-0 flex flex-col ${sidebarClassName ?? 'bg-tokens-neutral-bg-grey-2'}`}
          style={{ width: `${sidebarWidth}px` }}
        >
          <div className='h-[80px] border-b border-tokens-neutral-border-2 flex items-center px-6'>
            {sidebarHeader}
          </div>
          <div className='flex-1 overflow-y-auto py-2'>{sidebar}</div>
        </div>

        {isResizable ? (
          <div
            className='w-2 flex-shrink-0 cursor-col-resize hover:bg-tokens-neutral-border-colored transition-colors relative group'
            onMouseDown={handleMouseDown}
          >
            <div className='absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-tokens-neutral-border-1 group-hover:bg-tokens-primary-2' />
          </div>
        ) : null}

        {/* Right pane */}
        <div className='flex-1 flex flex-col overflow-hidden'>
          <div className='h-[80px] border-b border-tokens-neutral-border-2 flex items-center justify-between px-6 py-2 flex-shrink-0'>
            <div className='flex-1 min-w-0'>{headerLeft}</div>
            {headerRight ? (
              <div className='flex items-center gap-2 flex-shrink-0 ml-4'>{headerRight}</div>
            ) : null}
          </div>

          <div
            className={`flex-1 overflow-hidden ${contentClassName ?? 'bg-tokens-neutral-bg-grey-2'}`}
          >
            {content}
          </div>

          {footer === undefined ? (
            <DefaultFooter
              currentIndex={currentIndex}
              totalCount={totalCount}
              onPrev={onPrev}
              onNext={onNext}
              prevAriaLabel={prevAriaLabel}
              nextAriaLabel={nextAriaLabel}
            />
          ) : (
            footer
          )}
        </div>
      </div>
    </div>
  )
}

/** Reusable square close-X button matching the refactor viewer chrome. */
export function CloseButton({
  onClose,
  isDisabled = false
}: {
  onClose: () => void
  isDisabled?: boolean
}) {
  return (
    <button
      onClick={onClose}
      disabled={isDisabled}
      className='w-6 h-6 flex items-center justify-center hover:bg-tokens-neutral-fill-fill-6 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'
      aria-label='Close'
    >
      <svg
        width='24'
        height='24'
        viewBox='0 0 24 24'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M18 6L6 18M6 6l12 12'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </button>
  )
}
